import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { AutomatedSurveyEditRequest, AutomatedSurveyRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useCreateAutomatedSurvey = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: AutomatedSurveyRequest) => api.postAutomatedSurveysV1(payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.automated-surveys.new.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.automated-surveys.new.notification.error"),
      });
    },
  });
};

const useEditAutomatedSurvey = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: { surveyId: string; data: AutomatedSurveyEditRequest }) =>
      api.putAutomatedSurveysV1(payload.surveyId, payload.data).then((x) => x.data),
    onSuccess: (_, { surveyId }) => {
      showFlashToast({
        variant: "success",
        message: t("page.automated-surveys.edit.notification.success"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.AUTOMATED_SURVEY_DETAILS(surveyId),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.automated-surveys.edit.notification.error"),
      });
    },
  });
};

export const automatedSurveyMutations = {
  useCreateAutomatedSurvey,
  useEditAutomatedSurvey,
};
